import { isAxiosError } from 'axios';
import { sample, sampleSize } from 'lodash';
import { useCallback } from 'react';
import { mutationModes, mutationPositions, POPULATION_SIZE } from '../config/constants';
import Individual from '../types/individual';
import { createNewPopulationId, individualIsUnique, individualIsNonEmpty, makeNewIndividualBase } from '../utils/population';
import useApiClient from './useApiClient';
import useUserData from './useUserData';

export default function usePopulation() {
  const { population, setPopulation, apiKey, userDataIsReady: isReady } = useUserData();
  const { apiClient } = useApiClient();

  const toggleSelected = useCallback((id: string) => {
    setPopulation((prevPopulation) => {
      if (prevPopulation == null) return prevPopulation;
      return {
        ...prevPopulation,
        individuals: prevPopulation.individuals.map(
          (individual) =>
            individual.id === id ? { ...individual, selected: !individual.selected } : individual)
      };
    })
  }, [setPopulation]);

  const setImageUrl = useCallback((id: string, imageUrl: string | undefined) => {
    setPopulation((prevPopulation) => {
      if (prevPopulation == null) return prevPopulation;
      return {
        ...prevPopulation,
        individuals: prevPopulation.individuals.map(
          (individual) =>
            individual.id === id ? { ...individual, imageUrl } : individual)
      };
    })
  }, [setPopulation]);

  const setGenerationError = useCallback((id: string) => {
    setPopulation((prevPopulation) => {
      if (prevPopulation == null) return prevPopulation;
      return {
        ...prevPopulation,
        individuals: prevPopulation.individuals.map(
          (individual) =>
            individual.id === id ? { ...individual, generationError: true } : individual)
      };
    })
  }, [setPopulation]);

  const initPopulation = useCallback(async () => {
    const size = POPULATION_SIZE;
    const id = createNewPopulationId();

    // don't set the population to undefined because this triggers the effect
    const placeholderPopulation = {
      id,
      size,
      individuals: [],
    };
    setPopulation(placeholderPopulation);

    try {
      const { data, status, statusText } = await apiClient.post('/init', {
	size: POPULATION_SIZE,
	apiKey,
      });

      if (status !== 200)
	throw new Error(`HTTP error: ${status} (${statusText})`);

      const completion = data.content as string;

      if (completion == null)
	throw new Error(`empty response from /init`);

      const descriptions = completion.split(/\n+/);

      const newPopulation = {
	id,
	size,
	individuals: descriptions.map(
          (description) => (
            {
              ...makeNewIndividualBase(),
              genome: description.replace(/^\s*\d+\.\s*/, ''),
            }
          )
	),
      };
      setPopulation(newPopulation);
    } catch(error) {
      processApiError(error, apiKey); // don't throw an error here
      setPopulation(undefined);
    }
  }, [setPopulation, apiClient, apiKey]);

  const mutate = useCallback(
    async (individual: Individual) => {
      try {
	const { data, status, statusText } = await apiClient.post('/mutate', {
          input: individual.genome,
          mode: sample(mutationModes),
          position: sample(mutationPositions),
	  apiKey,
	});

	if (status !== 200)
          throw new Error(`HTTP error: ${status} (${statusText})`);

	const newGenome = data.output as string;

	// check something meaningful comes back from the API
	if (newGenome != null && newGenome.match(/\w/))
          individual.genome = data.output as string; // actually mutate the object!
      } catch(error) {
	throw processApiError(error, apiKey);
      }
    }, [apiClient, apiKey]
  );

  const crossover = useCallback(
    async (
      parents: Individual[],
    ) => {
      const mutationProbability = 1 / parents.length; // few parents means more mutation

      const [parent1, parent2] = sampleSize(parents, 2);

      try {
	const { data, status, statusText } = await apiClient.post('/crossover', {
          parent1: parent1.genome,
          parent2: parent2.genome,
	  apiKey,
	});

	if (status !== 200)
          throw new Error(`HTTP error: ${status} (${statusText})`);

	const child1 = {
          ...makeNewIndividualBase(),
          genome: data.child1 as string,
	};
	if (Math.random() < mutationProbability && individualIsNonEmpty(child1))
          await mutate(child1);

	const child2 = {
          ...makeNewIndividualBase(),
          genome: data.child2 as string,
	};
	if (Math.random() < mutationProbability && individualIsNonEmpty(child2))
          await mutate(child2);

	return [child1, child2];
      } catch(error) {
	throw processApiError(error, apiKey);
      }
    }, [apiClient, mutate, apiKey]
  );


  // replace the first placeholder in population.individuals (where individual.genome is nullish) with the new individual
  const replacePlaceholderWithIndividual = useCallback((individual: Individual) => {
    setPopulation((prevPopulation) => {
      if (prevPopulation == null) return;

      const newIndividuals = [...prevPopulation.individuals];
      const indexToReplace = newIndividuals.findIndex(ind => ind.genome == null);

      if (indexToReplace !== -1) {
        newIndividuals[indexToReplace] = individual;
      }

      return {
        ...prevPopulation,
        individuals: newIndividuals,
      };
    });
  }, [setPopulation]);

  const updatePopulation = useCallback(async () => {
    if (population == null) return;

    // get the parents and 'unselect' them ready for the next generation
    const parents = population.individuals
      .filter(({ selected }) => selected)
      .map((individual) => ({ ...individual, selected: false }));

    const placeholders = [...parents];
    while (placeholders.length < population.size)
      placeholders.push(makeNewIndividualBase());

    // update state for user-feedback
    setPopulation(
      (prevPopulation) => {
        if (prevPopulation == null) return;
        return {
          ...prevPopulation,
          individuals: placeholders,
        };
      }
    );

    try {
      // keep a 'local' copy of the population as it grows, for counting and uniqueness purposes
      const newIndividuals = [...parents];
      let tries = 0;
      while (newIndividuals.length < population.size) {
	if (tries++ > population.size * 2) break;
	// create two new offspring by recombination (plus occasional mutation) of two random parents
	// adds to offspring but only adds unique new individuals
	const [child1, child2] = await crossover(parents);

	if (individualIsNonEmpty(child1) && individualIsUnique(child1, newIndividuals)) {
          newIndividuals.push(child1);
          replacePlaceholderWithIndividual(child1);
	}

	// don't add the second child if the population is already full, sorry!
	if (
          newIndividuals.length < population.size &&
            individualIsNonEmpty(child2) && individualIsUnique(child2, newIndividuals)
	) {
          newIndividuals.push(child2);
          replacePlaceholderWithIndividual(child2);
	}
      }
    } catch(error) {
      // we already showed alerts about API key errors etc
      // so all we do now is wipe the population
      // and we should be good to go
      setPopulation(undefined);
    }

  }, [population, setPopulation, crossover, replacePlaceholderWithIndividual]);

  const isSelected = useCallback(
    (id: string) =>
      !!population?.individuals.find((individual) => individual.id === id && individual.selected),
    [population]
  );

  return {
    population,
    toggleSelected,
    isSelected,
    setImageUrl,
    setGenerationError,
    initPopulation,
    updatePopulation,
    isReady, // saved population, if present, has been retrieved from local storage
  };
}

function processApiError(error: unknown, apiKey: string | undefined): Error {
  let errorMessage: string;

  if (isAxiosError(error)) {
    if (!!apiKey && error.response?.status === 401) {
      errorMessage = 'There appears to be a problem with your OpenAI API key. Please check this in the settings panel.';
      alert(errorMessage);
    } else if (!apiKey && error.response?.status === 402) {
      console.error(`Axios error: ${error.response?.status} - ${error.response?.statusText}`);
      console.error(error.response?.data);
      errorMessage = error.response.data.error;
      alert(errorMessage);
    } else {
      console.error(`Axios error: ${error.response?.status} - ${error.response?.statusText}`);
      console.error(error.response?.data);
      errorMessage = `Error: ${error.response?.status} - ${error.response?.statusText}`;
      alert(errorMessage);
    }
  } else {
    // Other errors
    console.error('An unexpected error occurred:', error);
    errorMessage = 'An unexpected error occurred. Please try again later.';
    alert(errorMessage);
  }

  return new Error(errorMessage);
}
